<template>
  <!-- Background Container -->
  <div class="background-container" />

  <!-- Admin Dashboard Container -->
  <div class="admin-dashboard">
    <!-- Header Section -->
    <header class="app-header">
      <div class="logo-container">
        <img
          src="../assets/StaffTrainingLogo.png"
          alt="Company Logo"
          class="company-logo"
        >
        <h1 class="header-title">
          Staff Training Inventory Management System
        </h1>
      </div>
      <div class="header-buttons">
        <button @click="navigateToQrScanner">
          Go to Scanner
        </button>
        <button
          class="profile-button"
          title="Profile"
          @click="navigateToProfile"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="white"
          >
            <path
              d="
                M12 2a5 5 0 1 1 0 10
                5 5 0 0 1 0-10zm0 12
                c-4.42 0-8 3.58-8 8h16
                c0-4.42-3.58-8-8-8z
              "
            />
          </svg>
        </button>
        <button @click="logout">
          Logout
        </button>
      </div>
    </header>

    <!-- Navigation Menu -->
    <nav class="nav-bar">
      <ul>
        <li><a href="#admin-dashboard">Admin Dashboard</a></li>
        <li><a href="#add-items-users">Add/Edit Items or Users</a></li>
        <li><a href="#consumables-section">Consumable Items</a></li>
        <li><a href="#reusables-section">Reusable Items</a></li>
        <li><a href="#logs-section">Inventory Logs</a></li>
        <li><a href="#users-section">Users</a></li>
      </ul>
    </nav>

    <!-- Main Content Container -->
    <div class="content">
      <!-- Admin Dashboard Section -->
      <div
        id="admin-dashboard"
        class="dashboard-section"
      >
        <h2>Admin Dashboard</h2>

        <!-- Search Filter Section -->
        <div class="search-filter">
          <label for="search">Search Inventory:</label>
          <input
            id="search"
            v-model="searchTerm"
            type="text"
            placeholder="Enter item name or description..."
            @input="filterInventory"
          >
        </div>
        <div class="calendar-button-section">
          <button
            class="calendar-button"
            @click="navigateToCalendar"
          >
            View Calendar - Checked-Out Equipment
          </button>
        </div>
      </div>

      <!-- Add New Items or Users Section Container -->
      <div
        id="add-items-users"
        class="dashboard-section add-items-users"
      >
        <div class="section-header">
          <h3>Add/Edit Items or Users</h3>
          <div class="button-group">
            <button @click="showForm('addItem')">
              Add Item
            </button>
            <button @click="showForm('addUser')">
              Add User
            </button>
            <button @click="showForm('importCSV')">
              Import Items
            </button>
            <button @click="exportCSV">
              Export CSV
            </button>
          </div>
        </div>

        <!-- Add/Edit Item Form -->
        <div
          v-if="visibleForm === 'addItem'"
          class="form-section"
        >
          <form
            ref="addItemForm"
            enctype="multipart/form-data"
            @submit.prevent="editMode ? updateItem() : addItem()"
          >
            <div class="form-row">
              <label for="name">Name:</label>
              <input
                id="name"
                v-model="newItem.name"
                type="text"
                required
              >
            </div>

            <div class="form-row">
              <label for="type">Type:</label>
              <select
                id="type"
                v-model="newItem.type"
                required
              >
                <option value="consumable">
                  Consumable
                </option>
                <option value="reusable">
                  Reusable
                </option>
              </select>
            </div>

            <div class="form-row">
              <label for="stock">Stock:</label>
              <input
                id="stock"
                v-model="newItem.stock"
                type="number"
                min="0"
                required
                @input="onStockChange(newItem.stock)"
              >
            </div>

            <div class="form-row">
              <label for="description">Description:</label>
              <input
                id="description"
                v-model="newItem.description"
                type="text"
              >
            </div>

            <!-- Conditional Fields for Consumable Items -->
            <div
              v-if="newItem.type === 'consumable'"
              class="form-row"
            >
              <label for="itemsPerBox">Items Per Box:</label>
              <input
                id="itemsPerBox"
                v-model="newItem.itemsPerBox"
                type="number"
                min="1"
                required
                @input="handleItemsPerBoxChange"
              >
            </div>

            <div
              v-if="newItem.type === 'consumable'"
              class="form-row"
            >
              <label for="boxesInStock">Boxes In Stock:</label>
              <input
                id="boxesInStock"
                v-model="newItem.boxesInStock"
                type="number"
                min="0"
                required
                @input="onBoxesInStockChange($event.target.value)"
              >
            </div>

            <!-- New Low Stock Threshold Field -->
            <div
              v-if="newItem.type === 'consumable'"
              class="form-row"
            >
              <label for="lowStockThreshold">Low Stock Threshold:</label>
              <input
                id="lowStockThreshold"
                v-model="newItem.lowStockThreshold"
                type="number"
              >
            </div>

            <!-- Conditional Fields for Reusable Items -->
            <div
              v-if="newItem.type === 'reusable'"
              class="form-row"
            >
              <label for="outOfCommission">Units Out of Commission:</label>
              <input
                id="outOfCommission"
                v-model.number="newItem.outOfCommission"
                type="number"
                :max="maxOutOfCommission"
                min="0"
                @input="validateOutOfCommission"
              >
            </div>

            <div
              v-if="newItem.type === 'reusable'"
              class="form-row"
            >
              <label for="underMaintenance">Units Under Maintenance:</label>
              <input
                id="underMaintenance"
                v-model.number="newItem.underMaintenance"
                type="number"
                :max="maxUnderMaintenance"
                min="0"
                @input="validateUnderMaintenance"
              >
            </div>

            <div class="form-row">
              <label for="equipmentStatus">Equipment Status:</label>
              <input
                id="equipmentStatus"
                v-model="newItem.equipmentStatus"
                type="text"
              >
            </div>

            <div class="form-row">
              <label for="image">Image:</label>
              <input
                id="image"
                type="file"
                @change="onFileChange"
              >
            </div>

            <div
              v-if="currentItem && currentItem.type === 'reusable'"
              class="form-row check-in-out-row"
            >
              <span class="check-in-out-text">
                <!-- Dynamically rendered text -->
                <template v-if="currentItem.hasUnresolvedLogs && currentItem.stock === 0">
                  You have unresolved check-outs for this item.
                </template>
                <template v-else-if="!currentItem.hasUnresolvedLogs && currentItem.stock > 0">
                  This item is available for check-out.
                </template>
                <template v-else-if="currentItem.hasUnresolvedLogs && currentItem.stock > 0">
                  You also have the following options:
                </template>
                <template v-else>
                  No actions available for this item.
                </template>
              </span>

              <!-- Buttons rendered dynamically -->
              <div class="check-in-out-buttons">
                <button
                  v-if="currentItem.hasUnresolvedLogs"
                  type="button"
                  class="btn-light-blue"
                  @click="openCheckInModal(currentItem)"
                >
                  Check In
                </button>
                <button
                  v-if="currentItem.stock > 0"
                  type="button"
                  class="btn-light-blue"
                  @click="openCheckOutModal(currentItem)"
                >
                  Check Out
                </button>
              </div>
            </div>

            <div class="form-buttons">
              <button type="submit">
                {{ editMode ? "Update Item" : "Add Item" }}
              </button>
              <button
                v-if="editMode"
                @click.prevent="resetNewItem"
              >
                Cancel Edit
              </button>
            </div>
          </form>
        </div>

        <!-- Create or Edit User Form -->
        <div
          v-if="visibleForm === 'addUser' || visibleForm === 'editUser'"
          class="form-section"
        >
          <form
            ref="editUserForm"
            @submit.prevent="visibleForm === 'addUser' ? createUser() : updateUser()"
          >
            <div class="form-row">
              <label for="newUserFirstName">First Name:</label>
              <input
                id="newUserFirstName"
                v-model="newUser.firstName"
                type="text"
                required
              >
            </div>

            <div class="form-row">
              <label for="newUserLastName">Last Name:</label>
              <input
                id="newUserLastName"
                v-model="newUser.lastName"
                type="text"
                required
              >
            </div>

            <div class="form-row">
              <label for="newUserEmail">Email:</label>
              <input
                id="newUserEmail"
                v-model="newUser.email"
                type="email"
                :disabled="visibleForm === 'editUser'"
                required
              >
            </div>

            <div
              v-if="visibleForm === 'addUser'"
              class="form-row"
            >
              <label for="newUserPassword">Password:</label>
              <input
                id="newUserPassword"
                v-model="newUser.password"
                type="password"
                required
              >
            </div>

            <div class="form-row">
              <label for="newUserRole">Role:</label>
              <select
                id="newUserRole"
                v-model="newUser.role"
                required
              >
                <option value="trainer">
                  Trainer
                </option>
                <option value="admin">
                  Admin
                </option>
              </select>
            </div>

            <div class="form-buttons">
              <button type="submit">
                {{ visibleForm === 'addUser' ? 'Create User' : 'Update User' }}
              </button>
              <button
                type="button"
                @click="cancelEdit"
              >
                Cancel
              </button>
            </div>
          </form>
          <p v-if="createUserError || updateUserError">
            {{ createUserError || updateUserError }}
          </p>
        </div>

        <!-- Import CSV Form -->
        <div
          v-if="visibleForm === 'importCSV'"
          class="form-section"
        >
          <h4>Import Inventory from CSV</h4>
          <div class="form-row">
            <input
              type="file"
              accept=".csv"
              class="file-input"
              @change="handleFileChange"
            >
            <button
              :disabled="!selectedFile"
              class="import-button"
              @click="importCSV"
            >
              Import CSV
            </button>
          </div>
        </div>
      </div>

      <!-- Consumable Items Table Section -->
      <div
        id="consumables-section"
        class="dashboard-section"
      >
        <div class="table-header">
          <h3>Consumable Items</h3>
          <div class="table-controls">
            <button
              class="filter-button"
              @click="toggleFilterConsumables"
            >
              Filter
            </button>
            <input
              v-if="showFilterConsumables"
              v-model="filterTermConsumables"
              type="text"
              class="filter-input"
              placeholder="Filter items..."
            >
            <select
              v-model="sortByConsumables"
              class="sort-dropdown"
            >
              <option value="">
                Sort By...
              </option>
              <option
                v-for="column in displayedColumnsConsumables"
                :key="column"
                :value="column"
              >
                {{ column }}
              </option>
            </select>
            <button
              class="sort-order-button narrow-toggle"
              @click="toggleSortOrder('consumables')"
            >
              {{ sortAscConsumables ? '⬆️' : '⬇️' }}
            </button>
            <button
              class="toggle-columns-button"
              @click="toggleColumns('consumables')"
            >
              {{ showAllColumnsConsumables ? 'Return' : 'Expand' }}
            </button>
          </div>
        </div>

        <!-- Scrollable Table Container -->
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th
                  v-for="column in displayedColumnsConsumables"
                  :key="column"
                >
                  {{ column }}
                </th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in limitedConsumables"
                :key="item.itemID"
              >
                <td
                  v-for="column in displayedColumnsConsumables"
                  :key="column"
                >
                  <!-- Render image if the column is 'imageUrl' -->
                  <img
                    v-if="column === 'imageUrl' && getImageUrl(item[column])"
                    :src="getImageUrl(item[column])"
                    alt="Item Image"
                    class="item-image"
                  >
                  <!-- Render QR code if the column is 'qrCode' -->
                  <img
                    v-else-if="column === 'qrCode' && item[column]"
                    :src="item[column]"
                    alt="QR Code"
                    class="qr-code-image"
                  >
                  <!-- Default text for other columns -->
                  <span v-else>{{ item[column] }}</span>
                </td>
                <!-- Equipment Status Column (Symbols) -->
                <td>
                  <span
                    v-if="item.equipmentStatus === 'in stock'"
                    class="status-icon available"
                    title="Available for use"
                  >
                    ✅ Available
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'in use'"
                    class="status-icon in-use"
                    title="Currently in use"
                  >
                    🔄 In Use
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'out of commission'"
                    class="status-icon out-of-commission"
                    title="Needs repair or not usable"
                  >
                    ❌ Out of Commission
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'under maintenance'"
                    class="status-icon maintenance"
                    title="Being serviced"
                  >
                    🔧 Maintenance
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'low stock'"
                    class="status-icon low-stock"
                    title="Low stock"
                  >
                    ⚠️ Low Stock
                  </span>
                </td>
                <!-- Actions Column -->
                <td>
                  <button @click="editItem(item)">
                    Edit
                  </button>
                  <button @click="confirmDelete(item.itemID)">
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="consumablesVisibleRows < filteredConsumables.length"
          class="show-more-button"
          @click="showMoreConsumables"
        >
          Show More
        </button>
      </div>

      <!-- Reusable Items Table Section -->
      <div
        id="reusables-section"
        class="dashboard-section"
      >
        <div class="table-header">
          <h3>Reusable Items</h3>
          <div class="table-controls">
            <button
              class="filter-button"
              @click="toggleFilterReusables"
            >
              Filter
            </button>
            <input
              v-if="showFilterReusables"
              v-model="filterTermReusables"
              type="text"
              class="filter-input"
              placeholder="Filter items..."
            >
            <select
              v-model="sortByReusables"
              class="sort-dropdown"
            >
              <option value="">
                Sort By...
              </option>
              <option
                v-for="column in displayedColumnsReusables"
                :key="column"
                :value="column"
              >
                {{ column }}
              </option>
            </select>
            <button
              class="sort-order-button narrow-toggle"
              @click="toggleSortOrder('reusables')"
            >
              {{ sortAscReusables ? '⬆️' : '⬇️' }}
            </button>
            <button
              class="toggle-columns-button"
              @click="toggleColumns('reusables')"
            >
              {{ showAllColumnsReusables ? 'Return' : 'Expand' }}
            </button>
          </div>
        </div>

        <!-- Scrollable Table Container -->
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th
                  v-for="column in displayedColumnsReusables"
                  :key="column"
                >
                  {{ column }}
                </th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in limitedReusables"
                :key="item.itemID"
                :data-item-id="item.itemID"
              >
                <td
                  v-for="column in displayedColumnsReusables"
                  :key="column"
                >
                  <img
                    v-if="column === 'imageUrl' && getImageUrl(item[column])"
                    :src="getImageUrl(item[column])"
                    alt="Item Image"
                    class="item-image"
                  >
                  <!-- Render QR code if the column is 'qrCode' -->
                  <img
                    v-else-if="column === 'qrCode' && item[column]"
                    :src="item[column]"
                    alt="QR Code"
                    class="qr-code-image"
                  >
                  <!-- Default text for other columns -->
                  <span v-else>{{ item[column] }}</span>
                </td>
                <!-- Equipment Status Column -->
                <td>
                  <span
                    v-if="item.equipmentStatus === 'in stock'"
                    class="status-icon available"
                    title="Available for use"
                  >
                    ✅ Available
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'in use'"
                    class="status-icon in-use"
                    title="Currently in use"
                  >
                    🔄 In Use
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'out of commission'"
                    class="status-icon out-of-commission"
                    title="Needs repair or not usable"
                  >
                    ❌ Out of Commission
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'under maintenance'"
                    class="status-icon maintenance"
                    title="Being serviced"
                  >
                    🔧 Maintenance
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'low stock'"
                    class="status-icon low-stock"
                    title="Low stock"
                  >
                    ⚠️ Low Stock
                  </span>
                  <span
                    v-else-if="item.equipmentStatus === 'partially available'"
                    class="status-icon partially-available"
                    title="Partially available"
                  >
                    ⚙️ Partially Available
                  </span>
                </td>

                <!-- Low Stock Threshold Column -->
                <td>
                  <span>{{ item.lowStockThreshold }}</span>
                </td>
                <td>
                  <button @click="editItem(item)">
                    Edit
                  </button>
                  <button @click="confirmDelete(item.itemID)">
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="reusablesVisibleRows < filteredReusables.length"
          class="show-more-button"
          @click="showMoreReusables"
        >
          Show More
        </button>
      </div>

      <!-- Inventory Logs Table Section -->
      <div
        id="logs-section"
        class="dashboard-section"
      >
        <h3>Inventory Logs</h3>

        <!-- Table Controls for Filtering and Sorting -->
        <div class="table-controls">
          <input
            v-model="filterTermLogs"
            type="text"
            placeholder="Filter by general terms..."
          >
          <select v-model="filterResolved">
            <option value="">
              All
            </option>
            <option value="resolved">
              Resolved
            </option>
            <option value="unresolved">
              Unresolved
            </option>
          </select>
          <input
            v-model="filterFirstName"
            type="text"
            placeholder="Filter by first name..."
          >
          <div class="sort-controls">
            <label for="sort-options">Sort By:</label>
            <select
              id="sort-options"
              v-model="sortByLogs"
            >
              <option value="timestamp">
                Date
              </option>
              <option value="user_email">
                User Email
              </option>
              <option value="action">
                Action
              </option>
              <option value="resolved">
                Resolved
              </option>
              <option value="firstName">
                First Name
              </option>
            </select>
            <button @click="sortAscLogs = !sortAscLogs">
              {{ sortAscLogs ? 'Ascending ▲' : 'Descending ▼' }}
            </button>
          </div>
          <button @click="toggleExpandAll">
            {{ showAllColumns ? 'Collapse Columns' : 'Expand Columns' }}
          </button>
        </div>

        <!-- Table -->
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th @click="toggleSortLogs('log_id')">
                  Log ID {{ sortByLogs === 'log_id' && sortAscLogs ? '▲' : '▼' }}
                </th>
                <th @click="toggleSortLogs('item_id')">
                  Item ID {{ sortByLogs === 'item_id' && sortAscLogs ? '▲' : '▼' }}
                </th>
                <th @click="toggleSortLogs('user_email')">
                  User Email {{ sortByLogs === 'user_email' && sortAscLogs ? '▲' : '▼' }}
                </th>
                <th @click="toggleSortLogs('action')">
                  Action {{ sortByLogs === 'action' && sortAscLogs ? '▲' : '▼' }}
                </th>
                <th>Quantity</th>
                <th v-if="showAllColumns">
                  Description
                </th>
                <th @click="toggleSortLogs('timestamp')">
                  Timestamp {{ sortByLogs === 'timestamp' && sortAscLogs ? '▲' : '▼' }}
                </th>
                <th v-if="showAllColumns">
                  Transaction ID
                </th>
                <th v-if="showAllColumns">
                  Related Log ID
                </th>
                <th v-if="showAllColumns">
                  First Name
                </th>
                <th v-if="showAllColumns">
                  Last Name
                </th>
                <th v-if="showAllColumns">
                  Resolution Timestamp
                </th>
                <th v-if="showAllColumns">
                  Resolved
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in visibleLogs"
                :key="log.log_id"
              >
                <td>{{ log.log_id }}</td>
                <td>{{ log.item_id }}</td>
                <td>{{ log.user_email }}</td>
                <td>{{ log.action }}</td>
                <td>{{ log.quantity }}</td>
                <td v-if="showAllColumns">
                  {{ log.description }}
                </td>
                <td>{{ formatDate(log.timestamp) }}</td>
                <td v-if="showAllColumns">
                  {{ log.transactionID }}
                </td>
                <td v-if="showAllColumns">
                  {{ log.relatedLogID }}
                </td>
                <td v-if="showAllColumns">
                  {{ log.firstName }}
                </td>
                <td v-if="showAllColumns">
                  {{ log.lastName }}
                </td>
                <td v-if="showAllColumns">
                  {{ formatDate(log.resolutionTimestamp) }}
                </td>
                <td v-if="showAllColumns">
                  {{ log.resolved ? 'Yes' : 'No' }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Show More Button -->
          <button
            v-if="visibleLogs.length > 0 && logsVisibleRows < inventoryLogs.length"
            @click="showMoreLogs"
          >
            Show More Logs
          </button>
        </div>
      </div>
      <!-- Users Table Section -->
      <div
        id="users-section"
        class="dashboard-section"
      >
        <h3>Users</h3>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>User ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in limitedUsers"
                :key="user.id"
              >
                <td>{{ user.id }}</td>
                <td>{{ user.firstName }}</td>
                <td>{{ user.lastName }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.role }}</td>
                <td>
                  <div class="action-buttons">
                    <button @click="editUser(user)">
                      Edit
                    </button>
                    <button @click="confirmDeleteUser(user.id)">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            v-if="usersVisibleRows < users.length"
            @click="showMoreUsers"
          >
            Show More
          </button>
        </div>
      </div>

      <div
        v-if="showOrientationPrompt"
        class="orientation-modal"
      >
        <div class="orientation-modal-content">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            width="64"
            height="64"
            fill="#00A3DB"
          >
            <path
              d="
                M338.13,82.2C398.08,110.62,441,168.73,447.58,237.67h27.5
                C465.73,124.73,371.32,36,256,36l-12.1.55,69.85,69.85ZM223.55,68.08
                a27.35,27.35,0,0,0-38.87,0L68.08,184.68a27.35,27.35,0,0,0,0,38.87
                L288.45,443.92a27.35,27.35,0,0,0,38.87,0l116.6-116.6
                a27.35,27.35,0,0,0,0-38.87Zm84.33,356.4L87.52,204.12
                l116.6-116.6L424.48,307.88Zm-134,5.32A192.25,192.25,0,0,1,64.42,274.33
                H36.92C46.27,387.27,140.68,476,256,476l12.1-.55L198.25,405.6Z
              "
            />
          </svg>
          <p>{{ orientationMessage }}</p>
        </div>
      </div>

      <!-- CheckOut/In Modal -->
      <div
        v-if="showModal && currentItem"
        class="modal"
      >
        <div v-if="!showSuccessState">
          <!-- Original Modal Content -->
          <h3>{{ modalTitle }}</h3>
          <p>{{ modalMessage }}</p>

          <label for="quantity">Enter Quantity:</label>
          <input
            id="quantity"
            v-model="modalQuantity"
            type="number"
            min="1"
          >

          <button
            type="button"
            class="btn-primary"
            @click="submitModalAction"
          >
            {{ modalAction === 'checkIn' ? 'Confirm Check In' : 'Confirm Check Out' }}
          </button>
          <button
            type="button"
            class="btn-secondary"
            @click="closeModal"
          >
            Cancel
          </button>
        </div>
        <div v-else>
          <!-- Success State Content -->
          <h3>Success</h3>
          <p>{{ modalSuccessMessage }}</p>

          <button
            type="button"
            class="btn-primary"
            @click="handleSuccessOk"
          >
            OK
          </button>
        </div>
      </div>

      <!-- Footer -->
      <footer class="app-footer">
        <div class="footer-content">
          <img
            src="@/assets/ParentLogo.png"
            alt="Parent Company Logo"
            class="footer-logo"
          >
          <p>© 2024 Staff Nursing Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Papa from 'papaparse';

export default {
  name: 'AdminDashboard',
  data() {
    return {
      showModal: false, // Controls modal visibility
      modalTitle: '', // Title of the modal
      modalMessage: '', // Main message in the modal
      modalQuantity: null, // Quantity entered in the modal
      modalAction: '', // 'checkIn' or 'checkOut'
      modalSuccessMessage: '', // New success message
      showSuccessState: false, // Toggle for success state
      currentItem: null, // The item being acted upon
      consumablesVisibleRows: 10,
      reusablesVisibleRows: 10,
      logsVisibleRows: 10,
      usersVisibleRows: 10,
      showAllColumnsConsumables: false,
      showAllColumnsReusables: false,
      inventory: [],
      inventoryLogs: [],
      showAllColumns: false,
      users: [],
      allColumns: [],
      displayedColumnsConsumables: [],
      displayedColumnsReusables: [],
      newItem: {
        itemID: null,
        name: '',
        type: '',
        stock: 0,
        description: '',
        itemsPerBox: null,
        boxesInStock: null,
        lowStockThreshold: null,
        outOfCommission: 0,
        underMaintenance: 0,
        equipmentStatus: 'in stock',
        imageUrl: null,
        qrCode: '',
        lastCheckedOutBy: '',
        lastCheckedOutTime: '',
        lastCheckedInBy: '',
        lastCheckedInTime: '',
      },
      selectedImage: null,
      selectedFile: null,
      editMode: false,
      errorMessage: '',
      searchTerm: '',
      newUser: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'trainer',
      },
      createUserError: '',
      visibleForm: '',
      showFilterConsumables: false,
      showFilterReusables: false,
      filterTermConsumables: '',
      filterTermReusables: '',
      filterTermLogs: '',
      filterResolved: '', // Filter by resolved status
      filterFirstName: '', // Filter by first name
      sortByConsumables: '',
      sortAscConsumables: true,
      sortByReusables: '',
      sortAscReusables: true,
      sortByLogs: 'timestamp',
      sortAscLogs: true,
      isInitializingForm: false,
      totalStock: 0, // Add totalStock to the data section
      showOrientationPrompt: false,
      orientationMessage: '',
    };
  },
  computed: {
  // Limited Users for pagination
    limitedUsers() {
      return this.users.slice(0, this.usersVisibleRows);
    },

    // Limited Consumables for pagination
    limitedConsumables() {
      return this.filteredConsumables.slice(0, this.consumablesVisibleRows);
    },

    // Filtered Consumables with sorting logic
    filteredConsumables() {
      let items = this.inventory.filter(
        (item) => item.type.toLowerCase() === 'consumable',
      );

      // Apply filter if term is provided
      if (this.filterTermConsumables) {
        const filterTerm = this.filterTermConsumables.toLowerCase();
        items = items.filter((item) => item.name.toLowerCase().includes(filterTerm)
        || (item.lastCheckedOutBy && item.lastCheckedOutBy.toLowerCase().includes(filterTerm))
        || (item.equipmentStatus && item.equipmentStatus.toLowerCase().includes(filterTerm))
        || (item.lastCheckedOutTime && item.lastCheckedOutTime.toLowerCase().includes(filterTerm))
        || (item.lastCheckedInBy && item.lastCheckedInBy.toLowerCase().includes(filterTerm))
        || (item.lastCheckedInTime && item.lastCheckedInTime.toLowerCase().includes(filterTerm))
        || (item.description && item.description.toLowerCase().includes(filterTerm)));
      }

      // Sorting Logic
      if (this.sortByConsumables) {
        items = items.sort((a, b) => {
          if (this.sortAscConsumables) {
            return a[this.sortByConsumables] > b[this.sortByConsumables] ? 1 : -1;
          }
          return a[this.sortByConsumables] < b[this.sortByConsumables] ? 1 : -1;
        });
      }

      return items;
    },

    // Limited Reusables for pagination
    limitedReusables() {
      return this.filteredReusables.slice(0, this.reusablesVisibleRows);
    },

    // Filtered Reusables with sorting logic
    filteredReusables() {
      let items = this.inventory.filter(
        (item) => item.type.toLowerCase() === 'reusable',
      );

      // Apply filter if term is provided
      if (this.filterTermReusables) {
        const filterTerm = this.filterTermReusables.toLowerCase();
        items = items.filter((item) => item.name.toLowerCase().includes(filterTerm)
        || (item.lastCheckedOutBy && item.lastCheckedOutBy.toLowerCase().includes(filterTerm))
        || (item.equipmentStatus && item.equipmentStatus.toLowerCase().includes(filterTerm))
        || (item.lastCheckedOutTime && item.lastCheckedOutTime.toLowerCase().includes(filterTerm))
        || (item.lastCheckedInBy && item.lastCheckedInBy.toLowerCase().includes(filterTerm))
        || (item.lastCheckedInTime && item.lastCheckedInTime.toLowerCase().includes(filterTerm))
        || (item.description && item.description.toLowerCase().includes(filterTerm))
        || (item.outOfCommission
         && String(item.outOfCommission).toLowerCase().includes(filterTerm))
        || (item.underMaintenance
         && String(item.underMaintenance).toLowerCase().includes(filterTerm)));
      }

      // Sorting Logic
      if (this.sortByReusables) {
        items = items.sort((a, b) => {
          if (this.sortAscReusables) {
            return a[this.sortByReusables] > b[this.sortByReusables] ? 1 : -1;
          }
          return a[this.sortByReusables] < b[this.sortByReusables] ? 1 : -1;
        });
      }

      return items;
    },

    // Limited Logs for pagination
    limitedLogs() {
      return this.getFilteredLogs().slice(0, this.logsVisibleRows);
    },

    // Computed values for maximum limits on Out of Commission and Under Maintenance
    maxOutOfCommission() {
      return Math.max(this.totalStock - (this.newItem.underMaintenance || 0), 0);
    },
    maxUnderMaintenance() {
      return Math.max(this.totalStock - (this.newItem.outOfCommission || 0), 0);
    },
    visibleLogs() {
      let logs = this.inventoryLogs;

      // Apply search term filter
      if (this.filterTermLogs) {
        const filterTerm = this.filterTermLogs.toLowerCase();
        logs = logs.filter(
          (log) => (log.item_id && log.item_id.toLowerCase().includes(filterTerm))
          || (log.user_email && log.user_email.toLowerCase().includes(filterTerm))
          || (log.action && log.action.toLowerCase().includes(filterTerm))
          || (log.description && log.description.toLowerCase().includes(filterTerm))
          || (log.timestamp && log.timestamp.toLowerCase().includes(filterTerm)),
        );
      }

      // Apply resolved filter
      if (this.filterResolved === 'resolved') {
        logs = logs.filter((log) => log.resolved === 1);
      } else if (this.filterResolved === 'unresolved') {
        logs = logs.filter((log) => log.resolved === 0);
      }

      // Apply first name filter
      if (this.filterFirstName) {
        const firstNameFilter = this.filterFirstName.toLowerCase();
        logs = logs.filter(
          (log) => log.firstName && log.firstName.toLowerCase().includes(firstNameFilter),
        );
      }

      // Sorting logic
      if (this.sortByLogs) {
        logs = logs.sort((a, b) => {
          if (this.sortByLogs === 'resolved') {
            return this.sortAscLogs ? a.resolved - b.resolved : b.resolved - a.resolved;
          } if (this.sortByLogs === 'firstName') {
            return this.sortAscLogs
              ? a.firstName.localeCompare(b.firstName)
              : b.firstName.localeCompare(a.firstName);
          }
          if (this.sortAscLogs) {
            if (a[this.sortByLogs] > b[this.sortByLogs]) {
              return 1;
            }
            return -1;
          }
          if (a[this.sortByLogs] < b[this.sortByLogs]) {
            return 1;
          }
          return -1;
        });
      }

      return logs.slice(0, this.logsVisibleRows);
    },
  },

  watch: {
    // eslint-disable-next-line func-names
    'newItem.outOfCommission': function (newVal, oldVal) {
      if (this.isInitializingForm || newVal === oldVal) return;

      // Ensure outOfCommission + underMaintenance <= totalStock
      if ((newVal + this.newItem.underMaintenance) > this.totalStock) {
        this.newItem.outOfCommission = this.totalStock - this.newItem.underMaintenance;
      }

      // Adjust stock live
      const difference = newVal - oldVal;
      this.adjustStock(-difference);
      this.updateEquipmentStatus();
    },

    // eslint-disable-next-line func-names
    'newItem.underMaintenance': function (newVal, oldVal) {
      if (this.isInitializingForm || newVal === oldVal) return;

      // Ensure outOfCommission + underMaintenance <= totalStock
      if ((newVal + this.newItem.outOfCommission) > this.totalStock) {
        this.newItem.underMaintenance = this.totalStock - this.newItem.outOfCommission;
      }

      // Adjust stock live
      const difference = newVal - oldVal;
      this.adjustStock(-difference);
      this.updateEquipmentStatus();
    },
    $route() {
      this.checkOrientation();
    },
    showOrientationPrompt() {
    },

  },

  created() {
    this.fetchItems();
    this.fetchInventoryLogs();
    this.fetchUsers();
    this.setInitialColumns();
    this.checkOrientation();
    window.addEventListener('orientationchange', this.checkOrientation);
  },

  mounted() {
  // Add smooth scrolling with offset for navigation links
    document.querySelectorAll('.nav-bar a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault(); // Prevent default anchor behavior
        const targetId = link.getAttribute('href').substring(1); // Get the ID from the href
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          const offset = 120; // Adjust this value to your header + nav-bar height
          const position = targetElement.offsetTop - offset;

          // Smoothly scroll to the adjusted position
          window.scrollTo({
            top: position,
            behavior: 'smooth',
          });
        }
      });
    });
    // Fetch inventory logs on mount
    this.fetchInventoryLogs();
  },

  beforeUnmount() {
    window.removeEventListener('orientationchange', this.checkOrientation);
  },

  methods: {
    checkOrientation() {
      setTimeout(() => {
        const mediaQueryPortrait = window.matchMedia('(orientation: portrait)');
        const mediaQueryLandscape = window.matchMedia('(orientation: landscape)');
        const isPortrait = mediaQueryPortrait.matches;
        const isLandscape = mediaQueryLandscape.matches;

        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop) {
          this.showOrientationPrompt = false;
          return;
        }

        if (this.$route.name === 'AdminDashboard' && isPortrait) {
          this.showOrientationPrompt = true;
          this.orientationMessage = 'Please switch to landscape mode for better viewing.';
        } else if (this.$route.name === 'QrScanner' && isLandscape) {
          this.showOrientationPrompt = true;
          this.orientationMessage = 'Please switch to portrait mode for scanning.';
        } else {
          this.showOrientationPrompt = false; // Clear prompt if orientation is correct
        }
      }, 100);
    },

    // Open the Check In modal
    openCheckInModal(item) {
      this.modalTitle = `Check In ${item.name}`;
      this.modalMessage = `You are about to check in items for ${item.name}. Please enter the quantity.`;
      this.modalAction = 'checkIn';
      this.modalQuantity = null;
      this.currentItem = item;
      this.showModal = true; // Open the modal
    },

    openCheckOutModal(item) {
      this.modalTitle = `Check Out ${item.name}`;
      this.modalMessage = `You are about to check out items for ${item.name}. Please enter the quantity.`;
      this.modalAction = 'checkOut';
      this.modalQuantity = null;
      this.currentItem = item;
      this.showModal = true; // Open the modal
    },

    // Submit the Check In/Out action
    async submitModalAction() {
      if (!this.modalQuantity || this.modalQuantity <= 0) {
        this.$toast.error('Please enter a valid quantity.');
        return;
      }

      if (!this.currentItem || !this.modalAction) {
        this.$toast.error('Invalid modal state. Please try again.');
        return;
      }

      try {
        const payload = {
          itemID: this.currentItem.itemID,
          action: this.modalAction,
          quantity: this.modalQuantity,
        };

        // Handle reusable item logic for Check-In
        if (this.currentItem.type === 'reusable' && this.modalAction === 'checkIn') {
          const unresolvedLogIDs = await this.fetchUnresolvedLogs(this.currentItem.itemID);

          if (unresolvedLogIDs.length < this.modalQuantity) {
            this.$toast.error('Not enough unresolved logs to perform Check In.');
            return;
          }

          payload.unresolvedLogIDs = unresolvedLogIDs.slice(0, this.modalQuantity);
        }

        // Send the request to the backend
        const response = await axios.post('https://api.inventory.stafftraining.me/api/updateStock', payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });

        if (response?.data?.success) {
          this.modalSuccessMessage = `${this.modalAction === 'checkIn' ? 'Check In' : 'Check Out'} was successful!`;
          this.showSuccessState = true; // Trigger the success state in the modal
        } else {
          const message = response?.data?.message || 'Failed to complete the action.';
          // eslint-disable-next-line no-alert
          alert(`Error: ${message}`);
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message || 'An unknown error occurred.';
        console.error('Error during action:', errorMessage);
        // eslint-disable-next-line no-alert
        alert(`Error: ${errorMessage}`);
      }
    },

    handleSuccessOk() {
      this.showSuccessState = false; // Reset the success state
      this.closeModal(); // Close the modal
      this.closeForm(); // Close the form
      this.fetchItems(); // Refresh inventory data
      const itemID = this.currentItem?.itemID;
      if (itemID) this.scrollToItem(itemID); // Scroll to the item
    },

    // Close the modal and reset its state
    closeModal() {
      this.showModal = false;
      this.modalTitle = '';
      this.modalMessage = '';
      this.modalQuantity = null;
      this.modalAction = '';

      // Use a small delay to ensure modal transitions finish
      setTimeout(() => {
        this.currentItem = null; // Reset the current item after modal closes
      }, 300);
    },

    closeForm() {
      this.editMode = false;
      this.visibleForm = null;
      this.newItem = {};
      this.currentItem = {};
      this.selectedImage = null;
    },

    scrollToItem(itemID) {
      if (!itemID) {
        console.warn('Invalid itemID for scrollToItem.');
        return;
      }
      const element = document.querySelector(`[data-item-id="${itemID}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    filterInventory() {
    // Update the consumable and reusable filter terms with the search term
      this.filterTermConsumables = this.searchTerm;
      this.filterTermReusables = this.searchTerm;
    },
    setInitialColumns() {
      this.allColumns = [
        'name',
        'imageUrl',
        'type',
        'description',
        'stock',
        'itemsPerBox',
        'boxesInStock',
        'qrCode',
        'equipmentStatus',
        'lastCheckedOutBy',
        'lastCheckedOutTime',
        'lastCheckedInBy',
        'lastCheckedInTime',
        'outOfCommission',
        'underMaintenance',
        'itemID',
      ];

      this.displayedColumnsConsumables = ['name', 'imageUrl', 'description', 'stock', 'equipmentStatus'];
      this.displayedColumnsReusables = ['name', 'imageUrl', 'description', 'stock', 'equipmentStatus'];
    },
    toggleColumns(type) {
      if (type === 'consumables') {
        this.showAllColumnsConsumables = !this.showAllColumnsConsumables;
        this.displayedColumnsConsumables = this.showAllColumnsConsumables
          ? ['name', 'imageUrl', 'description', 'qrCode', 'stock', 'lowStockThreshold', 'itemsPerBox', 'boxesInStock', 'lastCheckedOutBy', 'lastCheckedOutTime', 'itemID']
          : ['name', 'imageUrl', 'description', 'stock', 'equipmentStatus'];
      } else if (type === 'reusables') {
        this.showAllColumnsReusables = !this.showAllColumnsReusables;
        this.displayedColumnsReusables = this.showAllColumnsReusables
          ? ['name', 'imageUrl', 'description', 'stock', 'qrCode', 'outOfCommission', 'underMaintenance', 'lastCheckedOutBy', 'lastCheckedOutTime', 'lastCheckedInBy', 'lastCheckedInTime', 'itemID']
          : ['name', 'imageUrl', 'description', 'stock', 'equipmentStatus'];
      }
    },

    toggleFilterConsumables() {
      this.showFilterConsumables = !this.showFilterConsumables;
    },

    // Method to toggle sorting order for consumables, reusables, or logs
    toggleSortOrder(type, field) {
      if (type === 'consumables') {
        if (this.sortByConsumables === field) {
          this.sortAscConsumables = !this.sortAscConsumables;
        } else {
          this.sortByConsumables = field;
          this.sortAscConsumables = true;
        }
      } else if (type === 'reusables') {
        if (this.sortByReusables === field) {
          this.sortAscReusables = !this.sortAscReusables;
        } else {
          this.sortByReusables = field;
          this.sortAscReusables = true;
        }
      } else if (type === 'logs') {
        if (this.sortByLogs === field) {
          this.sortAscLogs = !this.sortAscLogs;
        } else {
          this.sortByLogs = field;
          this.sortAscLogs = true;
        }
      }
    },

    // Method to increase the number of visible consumables rows
    showMoreConsumables() {
      this.consumablesVisibleRows += 10; // Increase the number of rows shown by 10
    },

    toggleFilterReusables() {
      this.showFilterReusables = !this.showFilterReusables;
    },

    // Method to increase the number of visible consumables rows
    showMoreReusables() {
      this.reusablesVisibleRows += 10; // Increase the number of rows shown by 10
    },

    async fetchItems() {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getAllItems', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        this.inventory = response.data.items.map((item) => {
          if (item.qrCode && !item.qrCode.startsWith('data:image/')) {
            // eslint-disable-next-line no-param-reassign
            item.qrCode = `data:image/png;base64,${item.qrCode}`;
          }

          if (item.imageUrl && !item.imageUrl.startsWith('http')) {
            // eslint-disable-next-line no-param-reassign
            item.imageUrl = `https://api.inventory.stafftraining.me/uploads/${item.imageUrl}`;
          }

          if (!item.imageUrl) {
            // eslint-disable-next-line no-param-reassign
            item.imageUrl = 'https://api.inventory.stafftraining.me/uploads/placeholder.png';
          }

          return item;
        });

        // Refresh the current item and form state, if applicable
        if (this.currentItem) {
          const updatedItem = this.inventory.find(
            (item) => item.itemID === this.currentItem.itemID,
          );
          if (updatedItem) {
            this.newItem = { ...updatedItem }; // Update form with refreshed data
            this.currentItem = { ...updatedItem }; // Update currentItem with refreshed data
          }
        }
      } catch (error) {
        console.error('Error fetching items:', error);
        this.errorMessage = 'An error occurred while fetching inventory items.';
      }
    },
    async fetchItemDetails(itemID) {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getItemDetails', {
          params: { itemID },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data && response.data.success) {
          this.newItem = response.data.item; // Populate the form with item details
          this.currentItem = { ...response.data.item }; // No longer include hasUnresolvedLogs here
        } else {
          this.$toast.error('Error fetching item details.');
        }
      } catch (error) {
        console.error('Error fetching item details:', error.response?.data || error.message);
        this.$toast.error('Error fetching item details.');
      }
    },

    async fetchUnresolvedLogs(itemID) {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getUnresolvedLogs', {
          params: { itemID },
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });

        if (response.data?.success && Array.isArray(response.data.logs)) {
          // eslint-disable-next-line no-console
          console.log('Unresolved logs fetched:', response.data.logs);
          return response.data.logs.map((log) => log.log_ID); // Extract and return log IDs
        }

        const message = response.data?.message || 'Failed to fetch unresolved logs.';
        this.$toast.error(message);
        console.warn('Unresolved logs fetch failed:', message);
        return [];
      } catch (error) {
        // Add logging to debug the structure of the error object
        console.error('Error object received:', error);

        const errorMessage = error?.response?.data?.message || error.message || 'An unknown error occurred.';
        console.error('Error fetching unresolved logs:', errorMessage);
        this.$toast.error(`Error fetching unresolved logs: ${errorMessage}`);
        return [];
      }
    },

    toggleSortLogs(field) {
      if (this.sortByLogs === field) {
        this.sortAscLogs = !this.sortAscLogs; // Toggle sort order
      } else {
        this.sortByLogs = field; // Set new sort field
        this.sortAscLogs = true; // Default to ascending
      }
    },
    showMoreLogs() {
      this.logsVisibleRows += 10; // Increase number of rows shown by 10
    },
    toggleExpandAll() {
      this.showAllColumns = !this.showAllColumns;
    },
    async fetchInventoryLogs() {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getInventoryLogs', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        this.inventoryLogs = response.data.logs;
      } catch (error) {
        console.error('Error fetching inventory logs:', error);
        this.errorMessage = 'An error occurred while fetching inventory logs.';
      }
    },
    formatDate(date) {
      // Check if date is null, undefined, or the epoch start time
      if (!date || new Date(date).getTime() === 0) {
        return 'Unresolved'; // Replace with placeholder
      }
      return new Date(date).toLocaleString(); // Format valid date
    },
    // Method to update an existing user
    async updateUser() {
      try {
        // Check if the ID is valid before sending request

        const response = await axios.put('https://api.inventory.stafftraining.me/api/admin/updateUser', {
          userId: this.newUser.id, // Pass the user ID as part of the request body
          firstName: this.newUser.firstName,
          lastName: this.newUser.lastName,
          email: this.newUser.email,
          role: this.newUser.role,
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data.success) {
          // eslint-disable-next-line no-alert
          alert('User updated successfully.');
          await this.fetchUsers();
          this.visibleForm = null; // Hide the form after update
        } else {
          // eslint-disable-next-line no-alert
          alert('Failed to update user.');
        }
      } catch (error) {
        console.error('Error updating user:', error);
        // eslint-disable-next-line no-alert
        alert('An error occurred while updating the user.');
      }
    },
    editUser(user) {
      this.newUser = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
      };
      this.visibleForm = 'editUser';
      this.$nextTick(() => {
        this.scrollToForm('editUserForm');
      });
    },

    confirmDeleteUser(userId) {
      // eslint-disable-next-line no-alert
      const isConfirmed = window.confirm('Are you sure you want to delete this user?');

      if (isConfirmed) {
        this.deleteUser(userId);
      }
    },

    // Method to delete a user (calls backend)
    async deleteUser(userId) {
      try {
        const response = await axios.delete(`https://api.inventory.stafftraining.me/api/admin/deleteUser/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data.success) {
          // eslint-disable-next-line no-alert
          alert('User deleted successfully.');
          // Refresh users list after deletion
          await this.fetchUsers();
        } else {
          // eslint-disable-next-line no-alert
          alert('Failed to delete user.');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        // eslint-disable-next-line no-alert
        alert('An error occurred while deleting the user.');
      }
    },
    async fetchUsers() {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getAllUsers', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        this.users = response.data.users;
      } catch (error) {
        console.error('Error fetching users:', error);
        this.errorMessage = 'An error occurred while fetching users.';
      }
    },
    // Method to cancel edit operation and reset form
    cancelEdit() {
      this.newUser = {
        firstName: '',
        lastName: '',
        email: '',
        role: 'trainer',
      };
      this.visibleForm = null; // Hide the form
    },
    showMoreUsers() {
      this.usersVisibleRows += 10; // Show more rows, adjust as needed
    },
    showForm(form) {
      if (form === 'addItem') {
        this.editMode = false; // Make sure we reset to add mode
        this.resetNewItem(); // Clear the form
      }
      this.visibleForm = this.visibleForm === form ? '' : form;
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedImage = file; // Assign the selected file to this.selectedImage
      } else {
        this.selectedImage = null; // Clear the selection if no file is selected
      }
    },

    async addItem() {
      try {
        this.updateEquipmentStatus();
        const formData = new FormData();
        formData.append('name', this.newItem.name);
        formData.append('type', this.newItem.type);
        formData.append('stock', this.newItem.stock);
        formData.append('description', this.newItem.description || '');

        if (this.newItem.itemsPerBox) {
          formData.append('itemsPerBox', this.newItem.itemsPerBox);
        }
        if (this.newItem.boxesInStock) {
          formData.append('boxesInStock', this.newItem.boxesInStock);
        }
        if (this.newItem.lowStockThreshold) {
          formData.append('lowStockThreshold', this.newItem.lowStockThreshold);
        }
        if (this.newItem.outOfCommission) {
          formData.append('outOfCommission', this.newItem.outOfCommission);
        }
        if (this.newItem.underMaintenance) {
          formData.append('underMaintenance', this.newItem.underMaintenance);
        }

        formData.append('equipmentStatus', this.newItem.equipmentStatus);

        if (this.selectedImage) {
          formData.append('image', this.selectedImage);
        }
        if (this.newItem.qrCode) {
          formData.append('qrCode', this.newItem.qrCode);
        }

        const response = await axios.post('https://api.inventory.stafftraining.me/api/addItem', formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data.success) {
          // eslint-disable-next-line no-alert
          alert('Item added successfully');
          await this.fetchItems();
          this.resetNewItem();
        } else {
          this.errorMessage = 'Failed to add item.';
        }
      } catch (error) {
        console.error('Error adding item:', error);
        this.errorMessage = 'Error occurred while adding item.';
      }
    },

    getImageUrl(imageUrl) {
      if (imageUrl) {
        if (imageUrl.startsWith('http')) {
          return imageUrl;
        }

        return `https://api.inventory.stafftraining.me/uploads/${imageUrl}`;
      }

      return 'https://api.inventory.stafftraining.me/uploads/placeholder.png';
    },

    async updateItem() {
      try {
        // Remove form validation for OOC + UM exceeding original stock at submission
        this.updateEquipmentStatus();

        const formData = new FormData();
        formData.append('itemID', this.newItem.itemID);
        formData.append('name', this.newItem.name);
        formData.append('type', this.newItem.type);
        formData.append('stock', this.newItem.stock);
        formData.append('description', this.newItem.description || '');

        // Only append changed fields
        if (this.newItem.type === 'consumable') {
          if (this.newItem.itemsPerBox) {
            formData.append('itemsPerBox', this.newItem.itemsPerBox);
          }
          if (this.newItem.boxesInStock) {
            formData.append('boxesInStock', this.newItem.boxesInStock);
          }
          if (this.newItem.lowStockThreshold) {
            formData.append('lowStockThreshold', this.newItem.lowStockThreshold);
          }
        }

        if (this.newItem.type === 'reusable') {
          if (this.newItem.outOfCommission) {
            formData.append('outOfCommission', this.newItem.outOfCommission);
          }
          if (this.newItem.underMaintenance) {
            formData.append('underMaintenance', this.newItem.underMaintenance);
          }
        }

        formData.append('equipmentStatus', this.newItem.equipmentStatus);

        // Image handling logic:
        if (this.selectedImage) {
          // If a new image is selected, we add it to the form data
          formData.append('image', this.selectedImage);
        } else if (this.newItem.imageUrl && !this.newItem.imageUrl.includes('placeholder.png')) {
          formData.append('existingImageUrl', this.newItem.imageUrl);
        }

        if (this.newItem.qrCode) {
          formData.append('qrCode', this.newItem.qrCode);
        }

        const response = await axios.put('https://api.inventory.stafftraining.me/api/admin/updateItem', formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.success) {
          // eslint-disable-next-line no-alert
          alert('Item updated successfully');
          await this.fetchItems();
          this.resetNewItem();
        } else {
          this.errorMessage = 'Failed to update item.';
        }
      } catch (error) {
        console.error('Error updating item:', error);
        this.errorMessage = 'Error occurred while updating item.';
      }
    },

    validateOutOfCommission() {
    // Ensure outOfCommission + underMaintenance <= totalStock
      const maxAllowed = Math.max(this.totalStock - (this.newItem.underMaintenance || 0), 0);
      if (this.newItem.outOfCommission > maxAllowed) {
        this.newItem.outOfCommission = maxAllowed; // Cap to the max allowed value
      }
    },

    validateUnderMaintenance() {
    // Ensure outOfCommission + underMaintenance <= totalStock
      const maxAllowed = Math.max(this.totalStock - (this.newItem.outOfCommission || 0), 0);
      if (this.newItem.underMaintenance > maxAllowed) {
        this.newItem.underMaintenance = maxAllowed; // Cap to the max allowed value
      }
    },

    resetNewItem() {
      this.newItem = {
        itemID: null,
        name: '',
        type: '',
        stock: 0,
        description: '',
        itemsPerBox: null,
        boxesInStock: null,
        lowStockThreshold: null,
        outOfCommission: 0,
        underMaintenance: 0,
        equipmentStatus: 'in stock',
        imageUrl: null,
        qrCode: '',
      };
      this.selectedImage = null;
      this.editMode = false;
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file; // Update selected file to handle uploads
      }
    },

    async editItem(item) {
      // eslint-disable-next-line no-console
      console.log('Starting editItem with:', item);

      this.isInitializingForm = true;
      this.newItem = { ...item };
      this.selectedImage = null;
      this.editMode = true;
      this.visibleForm = 'addItem';

      this.currentItem = { ...item, hasUnresolvedLogs: false, unresolvedLogIDs: [] };

      this.totalStock = item.stock + (item.outOfCommission || 0) + (item.underMaintenance || 0);

      try {
        // eslint-disable-next-line no-console
        console.log('Fetching item details for:', item.itemID);
        await this.fetchItemDetails(item.itemID);

        if (this.newItem.type === 'reusable') {
          // eslint-disable-next-line no-console
          console.log('Fetching unresolved logs for reusable item:', item.itemID);

          // Safeguard against errors in fetchUnresolvedLogs
          const unresolvedLogIDs = await this.fetchUnresolvedLogs(item.itemID) || [];
          // eslint-disable-next-line no-console
          console.log('Fetched unresolved logs:', unresolvedLogIDs);

          this.currentItem.hasUnresolvedLogs = unresolvedLogIDs.length > 0;
          this.currentItem.unresolvedLogIDs = unresolvedLogIDs;
        }
      } catch (error) {
        console.error('Error during editItem process:', error);
        const errorMessage = error?.message || 'An error occurred during editItem.';
        this.$toast.error(`Failed to fetch item details or unresolved logs: ${errorMessage}`);
      } finally {
        if (this.newItem.type === 'consumable') {
          this.updateBoxesBasedOnStock();
        }
        this.isInitializingForm = false;
        this.scrollToForm('addItemForm');
      }
    },
    scrollToForm(refName) {
      const formElement = this.$refs[refName];
      if (formElement) {
        const offset = -200;
        const elementPosition = formElement.getBoundingClientRect().top + window.scrollY;
        const scrollPosition = elementPosition + offset;

        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      }
    },

    updateEquipmentStatus() {
      const { outOfCommission, underMaintenance, stock } = this.newItem;

      const totalUnavailable = (outOfCommission || 0) + (underMaintenance || 0);

      if (stock === 0 && totalUnavailable > 0) {
        // All units are unavailable
        if (outOfCommission > 0 && underMaintenance === 0) {
          this.newItem.equipmentStatus = 'out of commission';
        } else if (underMaintenance > 0 && outOfCommission === 0) {
          this.newItem.equipmentStatus = 'under maintenance';
        } else {
          this.newItem.equipmentStatus = 'partially available';
        }
      } else if (totalUnavailable > 0 && stock > totalUnavailable) {
        // Some units are unavailable, others still available
        this.newItem.equipmentStatus = 'partially available';
      } else if (stock > 0 && totalUnavailable === 0) {
        // All units are available
        this.newItem.equipmentStatus = 'in stock';
      } else if (stock === 0 && totalUnavailable === 0) {
        // All units are being used
        this.newItem.equipmentStatus = 'in use';
      }
    },

    adjustStock(amount) {
      if (this.isInitializingForm) {
        // If the form is being initialized, we don't adjust the stock
        return;
      }

      if (amount !== undefined) {
        // Adjust stock by a specific amount (positive or negative)
        this.newItem.stock = Math.max(this.newItem.stock + amount, 0);
      } else {
        // Recalculate available stock based on current unavailable units
        const totalUnavailable = (this.newItem.outOfCommission || 0) + (
          this.newItem.underMaintenance || 0);
        this.newItem.stock = Math.max(this.totalStock - totalUnavailable, 0);
      }
    },

    updateBoxesBasedOnStock() {
      if (this.newItem.type === 'consumable' && this.newItem.itemsPerBox > 0) {
        this.newItem.boxesInStock = Math.floor(this.newItem.stock / this.newItem.itemsPerBox);
      }
    },

    // Method to manually update stock based on boxesInStock change for consumables
    updateStockBasedOnBoxes() {
      if (this.newItem.type === 'consumable' && this.newItem.itemsPerBox > 0) {
        this.newItem.stock = this.newItem.boxesInStock * this.newItem.itemsPerBox;
      }
    },

    // Method triggered by user actions (input changes) to update values manually
    onStockChange(newStockValue) {
      if (this.newItem.type === 'consumable' && this.newItem.itemsPerBox > 0) {
        // Set the new stock value
        this.newItem.stock = newStockValue;

        // Calculate the number of complete boxes and remaining items
        this.newItem.boxesInStock = Math.floor(newStockValue / this.newItem.itemsPerBox);
        this.remainingItems = newStockValue % this.newItem.itemsPerBox;

        // Update the equipment status based on the new stock values
        this.updateEquipmentStatus();
      }
    },

    onBoxesInStockChange(newBoxesInStockValue) {
      if (this.newItem.type === 'consumable' && this.newItem.itemsPerBox > 0) {
        // Parse the new value to an integer to ensure calculations are correct
        const newBoxesInStock = parseInt(newBoxesInStockValue, 10);

        // Update boxesInStock in the state
        this.newItem.boxesInStock = newBoxesInStock;

        // Update stock based on the new boxes in stock, while keeping any remaining items in stock
        const remainingItems = this.newItem.stock % this.newItem.itemsPerBox;
        this.newItem.stock = (newBoxesInStock * this.newItem.itemsPerBox) + remainingItems;

        // Ensure that stock cannot be negative
        this.newItem.stock = Math.max(this.newItem.stock, 0);

        // Update the equipment status based on the new stock values
        this.updateEquipmentStatus();
      }
    },

    confirmDelete(itemID) {
    // Show confirmation dialog
      // eslint-disable-next-line no-alert
      const isConfirmed = window.confirm('Are you sure you want to delete this item?');

      if (isConfirmed) {
      // If confirmed, call the deleteItem method
        this.deleteItem(itemID);
      } else {
      // If not confirmed, do nothing (deletion is canceled)
      }
    },

    async deleteItem(itemID) {
      try {
        const response = await axios.delete(`https://api.inventory.stafftraining.me/api/deleteItem/${itemID}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        if (response.data.success) {
          // eslint-disable-next-line no-alert
          alert('Item deleted successfully');
          await this.fetchItems();
        } else {
          this.errorMessage = 'Failed to delete item.';
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        this.errorMessage = 'Error occurred while deleting item.';
      }
    },
    navigateToProfile() {
      this.$router.push({ name: 'ProfilePage' });
    },
    logout() {
      localStorage.removeItem('authToken');
      this.$router.push('/');
    },
    navigateToQrScanner() {
      this.$router.push({ name: 'QrScanner' });
    },
    navigateToCalendar() {
      this.$router.push({ name: 'CalendarView' });
    },
    async createUser() {
      try {
        const response = await axios.post('https://api.inventory.stafftraining.me/api/admin/createUser', this.newUser, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data.success) {
          // eslint-disable-next-line no-alert
          alert('User created successfully.');
          await this.fetchUsers();
          this.resetNewUser();
        } else {
          this.createUserError = response.data.message;
        }
      } catch (error) {
        console.error('Error creating user:', error);
        this.createUserError = 'Error occurred while creating user.';
      }
    },
    resetNewUser() {
      this.newUser = {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'trainer',
      };
      this.createUserError = '';
    },
    exportCSV() {
      if (!this.inventory || this.inventory.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No inventory data available to export.');
        return;
      }

      const csv = Papa.unparse(this.inventory);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'inventory.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    importCSV() {
      if (!this.selectedFile) {
        // eslint-disable-next-line no-alert
        alert('Please select a CSV file first.');
        return;
      }

      Papa.parse(this.selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const response = await axios.post('https://api.inventory.stafftraining.me/api/importItems', { items: results.data }, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              },
            });

            if (response.data.success) {
              // eslint-disable-next-line no-alert
              alert('Inventory imported successfully.');
              await this.fetchItems();
              this.selectedFile = null;
            } else {
              // eslint-disable-next-line no-alert
              alert('Failed to import inventory.');
            }
          } catch (error) {
            console.error('Error importing inventory:', error);
            // eslint-disable-next-line no-alert
            alert('An error occurred while importing the inventory.');
          }
        },
        error: (error) => {
          console.error('Error parsing CSV file:', error);
          // eslint-disable-next-line no-alert
          alert('Error parsing CSV file.');
        },
      });
    },
  },
};
</script>

<style scoped>
/* General Reset for Margin and Padding */
body, html {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

html, body {
  background-color: transparent !important; /* Force override any default styles */
}

/* Admin Dashboard Wrapper */
.admin-dashboard {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative; /* Needed for the layering of the background image */
  z-index: 1; /* Keeps it above the background */
}

/* Background Container - Separate it to ensure it doesn't interfere with other elements */
.background-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1; /* Keep background behind everything else */
  background: url('@/assets/ScannerBackground.png') no-repeat center center fixed;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  opacity: 0.3;
}

/* Header Styles */
.app-header {
  position: fixed; /* Keep the header fixed at the top */
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background-color: #262626; /* Dark blue/gray color */
  color: white;
  z-index: 1000; /* Ensure header is above other content */
  width: 100%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); /* Adding subtle shadow for better separation */
}

.logo-container {
  display: flex;
  align-items: center;
}

.company-logo {
  height: 60px;
}

.header-title {
  flex-grow: 1;
  margin-left: 20px;
  font-size: 1.5em;
}

.header-buttons {
  display: flex;
  gap: 20px; /* Space between buttons */
}

.header-buttons button {
  background-color: #00A3DB; /* Button color */
  color: white;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 12px;
}

.header-buttons button:hover {
  background-color: #2980b9; /* Darker blue on hover */
}

.profile-button {
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #00A3DB; /* Button background color */
}

.profile-button:hover {
  background-color: #007BA7; /* Slightly darker blue on hover */
}

.profile-button svg {
  fill: white; /* Icon color */
}
/* Page Navigation Sticky Bar */
.nav-bar {
  position: sticky; /* Makes it stick to the top */
  top: 100px;
  background-color: rgba(50, 50, 50, 0.4); /* Background color for the bar */
  padding: 6px 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); /* Slight shadow for visual appeal */
  z-index: 1000; /* Ensures it's above other elements */
}

.nav-bar ul {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  justify-content: center; /* Centers the navigation links */
}

.nav-bar li {
  margin: 0 12px;
}

.nav-bar a {
  text-decoration: none;
  color: #f5f3f3; /* Link color */
  font-weight: normal;
  font-size: 14px;
  transition: color 0.3s ease; /* Smooth hover effect */
}

.nav-bar a:hover {
  color: #0056b3; /* Darker color on hover */
}

/* Main Content Container */
.content {
  flex-grow: 1;
  padding: 140px 20px 20px 20px; /* Account for header height and padding */
  width: 80%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* Section Container Styling */
.dashboard-section {
  width: 100%;
  margin-bottom: 30px;
  padding: 20px;
  background-color: #F5F3F3; /* Light grey color */
  border-radius: 15px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
}

/* Section Titles */
.dashboard-section h2 {
  font-size: 1.8em;
  margin-bottom: 20px;
}

.dashboard-section h3,
.dashboard-section h4 {
  margin-bottom: 15px;
}

/* Search Filter Styling */
.search-filter {
  margin-top: 10px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.search-filter label {
  white-space: nowrap;
  font-size: 1.2em;
  margin-bottom: 20px;
}

/* Buttons for Form Sections */
.dashboard-section button {
  margin-top: 10px;
  background-color: #191732; /* Button color */
  color: white;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 8px;
}

.dashboard-section button:hover {
  background-color: #2980b9; /* Darker blue on hover */
}

/* Form Inputs and Labels */
.dashboard-section label {
  display: block;
  margin-top: 10px;
}

.dashboard-section input,
.dashboard-section select {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

/* Bulk Operations Styling */
.bulk-operations {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.export-button,
.import-button {
  width: fit-content;
}

/* Add New Items or Users Section */
.add-items-users {
  padding: 20px;
  border-radius: 10px;
  background-color: #f5f3f3;
  margin-bottom: 20px;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.form-section {
  margin-top: 20px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.form-row label {
  flex: 1;
}

.form-row input,
.form-row select {
  flex: 2;
  padding: 5px;
}

.form-buttons {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

.check-in-out-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
}

/* Style for the text */
.check-in-out-text {
  font-size: 1rem;
  color: #333;
  margin-right: 15px;
}

/* Style for the buttons */
.check-in-out-buttons button {
  padding: 8px 15px;
  margin-left: 10px;
  border: none;
  border-radius: 4px;
  background-color: #007bff; /* Light blue */
  color: #fff;
  cursor: pointer;
  font-size: 0.9rem;
}

.check-in-out-buttons button:hover {
  background-color: #0056b3; /* Slightly darker blue on hover */
}

/* Filter and Sort Controls Styling */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

/* Container for Filter and Sort Controls */
.table-controls {
  display: flex;
  gap: 10px; /* Space between buttons and filter input */
  align-items: center;
}

/* Button for Filtering */
.filter-button {
  background-color: #00A3DB;
  color: white;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 5px;
}

.filter-button:hover {
  background-color: #2980b9;
}

/* Sort and Filter Buttons Styling */
.filter-input {
  width: 150px; /* Adjust the width as per your requirements */
  box-sizing: border-box;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #ddd;
}
.sort-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
/* Sort Dropdown and Toggle Button */
.sort-dropdown {
  padding: 4px;
  border-radius: 5px;
  border: 1px solid #ddd;
}

.sort-button,
.sort-order-button {
  background-color: #00A3DB; /* Button color */
  color: white;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 5px;
}

.sort-button:hover,
.sort-order-button:hover {
  background-color: #2980b9; /* Darker blue on hover */
}

/* Scrollable Table Container Styling */
.qr-code-image {
  max-width: 100px; /* Adjust width to fit nicely within the table */
  height: auto;
  border-radius: 8px; /* Optional: Add rounded corners */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); /* Optional: Add a slight shadow for effect */
}

.table-container {
  overflow-x: auto;  /* Enables horizontal scroll when needed */
  -webkit-overflow-scrolling: touch;  /* Adds smooth scrolling for mobile devices */
  margin-top: 20px;
}

.table-container table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px; /* Ensure there's enough space for all columns */
}

th,
td {
  border: 1px solid #ddd;
  padding: 8px;
}

th {
  background-color: #f2f2f2;
  text-align: left;
}

table tbody tr:nth-child(even) {
  background-color: #fafafa; /* Light grey for alternating rows */
}

/* Adjust image styling */
.item-image {
  max-width: 100px; /* Limit the width of images */
  height: auto;
}

.status-icon {
  font-size: 1.2em;
  margin-right: 8px;
}

.status-icon.available {
  color: green;
}

.status-icon.in-use {
  color: rgb(167, 167, 21);
}

.status-icon.out-of-commission {
  color: red;
}

.status-icon.maintenance {
  color: orange;
}

.status-icon.low-stock {
  color: #FFA500; /* Orange for warning */
}

.status-icon.partially-available {
  color: #ff9800; /* Orange color to indicate partial availability */
  font-weight: bold;
}

.show-more-button {
  margin-top: 15px;
  background-color: #00A3DB;
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 8px;
}

.show-more-button:hover {
  background-color: #2980b9;
}

.action-buttons {
  display: flex;
  gap: 10px; /* Adds 10px gap between the buttons */
}

/* Orientation Modal Styles */
.orientation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: auto;
}

.orientation-modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
  width: 90%;
}

.orientation-modal-content svg {
  margin-bottom: 15px;
}

.orientation-modal-content p {
  font-size: 16px;
  color: #333;
}

/* Modal container */
.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 400px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  padding: 20px;
}

/* Modal header */
.modal h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.25rem;
  color: #333;
}

/* Modal message */
.modal p {
  margin-bottom: 15px;
  font-size: 1rem;
  color: #555;
}

/* Modal input field */
.modal input[type="number"] {
  width: 100%;
  padding: 8px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}

/* Modal buttons */
.modal button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
}

.modal button.btn-primary {
  background-color: #007bff; /* Light blue */
  color: #fff;
}

.modal button.btn-primary:hover {
  background-color: #0056b3;
}

.modal button.btn-secondary {
  background-color: #e0e0e0;
  color: #333;
}

.modal button.btn-secondary:hover {
  background-color: #d6d6d6;
}

/* Footer Styles */
.app-footer {
  width: 100%;
  background-color: #262626; /* Dark blue/gray color */
  color: white;
  text-align: center;
  padding: 0px;
  margin-top: 20px; /* Add space above the footer */
  position: relative;
  z-index: 1;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.footer-logo {
  height: 60px;
  margin-bottom: 10px;
}

@media (max-width: 600px), (max-height: 1180px) {
  .header-title {
    font-size: 0.7em;
  }
  .header-buttons {
  gap: 8px; /* Space between buttons */
  }
  .header-buttons button {
  padding: 4px 8px;
  font-size: 0.7em;
  }
  .nav-bar a {
  font-size: 0.7em;
  }
}
</style>
